import { useEffect, useCallback } from 'react';
import { usePluginspace } from './usePluginspace';
import api from '../services/api';

/**
 * Interface para dados de notificação de fila
 */
export interface QueueNotification {
  queueId: number;
  phone: string;
  pluginspaceId: number;
  status: 'confirmed' | 'removed' | 'called' | 'completed';
  position?: number;
  estimatedTime?: number;
  tolerance?: number;
  arrivalTime?: string;
  message?: string;
  createdAt?: string;
  receivedAt?: string;
}

/**
 * NOTA: Este hook foi refatorado para usar notificações híbridas (Push + Email + SMS)
 * em vez de WebSocket. O WebSocket não funciona quando o app está fechado.
 * 
 * O sistema agora usa polling para verificar atualizações de fila periodicamente
 * e confia em notificações push, email e SMS do servidor.
 */

/**
 * Hook para gerenciar notificações de fila com sistema híbrido (Push + Email + SMS)
 * 
 * O sistema não usa mais WebSocket porque:
 * - WebSocket requer conexão ativa
 * - A conexão morre quando o app é fechado
 * - Notificações Push/Email/SMS chegam mesmo com app fechado
 * 
 * Este hook agora:
 * 1. Faz polling periodicamente para verificar atualizações
 * 2. Confia em notificações do servidor (Push/Email/SMS)
 * 3. Atualiza a UI quando recebe notificações
 */
export function useQueueNotifications(
  phone: string,
  pluginspaceId: string,
  onNotification?: (notification: QueueNotification) => void
) {
  const { getToken } = usePluginspace();

  /**
   * Polling: Verificar atualizações de fila periodicamente
   * Intervalo: 30 segundos (configurável)
   * 
   * MUDANÇA: Agora chama /queue-allocation/status (backend-staging)
   * ao invés de /queue-notifications/status (serviceMensageria)
   */
  const pollQueueStatus = useCallback(async () => {
    try {
      const token = await getToken();
      
      if (!token || !phone || !pluginspaceId) {
        console.log('⏭️ Polling pausado: Credenciais incompletas');
        return;
      }

      console.log('🔄 Polling: Verificando status da fila...');
      
      // Chamar endpoint de status do backend via HTTP
      const response = await api.get(
        `/api/queue-allocation/status/${pluginspaceId}/${phone}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.notification && onNotification) {
        console.log('📬 Nova notificação encontrada via polling:', response.data.notification);
        onNotification(response.data.notification);
      }
    } catch (error) {
      console.warn('⚠️ Erro ao fazer polling:', error);
    }
  }, [phone, pluginspaceId, getToken]);

  /**
   * Effect para configurar polling
   * Executa a cada 30 segundos enquanto o componente está montado
   */
  useEffect(() => {
    // Verificar logo na montagem
    pollQueueStatus();

    // Configurar polling periódico
    const pollInterval = setInterval(() => {
      pollQueueStatus();
    }, 30000); // 30 segundos

    return () => {
      clearInterval(pollInterval);
    };
  }, [pollQueueStatus]);

  /**
   * NOTA IMPORTANTE: Notificações em tempo real são entregues via:
   * 1. Push Notifications (quando app está aberto)
   * 2. Email (sempre confiável)
   * 3. SMS (para casos críticos)
   * 
   * Estas são controladas pelo servidor quando:
   * - Admin confirma posição na fila
   * - Admin remove cliente da fila
   * - Cliente é chamado
   * 
   * O servidor garante entrega mesmo com app fechado.
   */

  return {
    isConnected: true, // Sempre "conectado" via polling + push
    requestStatus: pollQueueStatus,
    disconnect: () => {}, // Noop - não há conexão para desconectar
    reconnect: pollQueueStatus,
  };
}
